import type { SupportedLanguage } from '@shared/core/localized-defaults'
import type { CalendarWithTemplate, MessageTemplateDto } from '@shared/core/message-templates'
import type { MessageTemplateOrigin } from 'src/modules/message-templates/types/message-template.types'

export type EventTemplateLanguageInput = {
  /** The card's current template pick; `null` once the appointment is customized. */
  selectedTemplateId: string | null | undefined
  event: {
    calendar?: string | { id?: string } | null
    message_template_id?: string | null
    template_language?: SupportedLanguage | null
  }
  templates: MessageTemplateDto[] | undefined
  calendarsWithTemplates: CalendarWithTemplate[] | undefined
}

export type EventTemplateLanguage = {
  language: SupportedLanguage | null
  /** `null` when no template sets the language, or the lists are not loaded yet. */
  origin: MessageTemplateOrigin | null
}

type EffectiveTemplate = { template: MessageTemplateDto; origin: MessageTemplateOrigin }

function findEffectiveTemplate({
  selectedTemplateId,
  event,
  templates,
  calendarsWithTemplates,
}: EventTemplateLanguageInput): EffectiveTemplate | null {
  const findTemplate = (id: string | null | undefined) => (id ? templates?.find((t) => t.id === id) : undefined)
  const picked = findTemplate(selectedTemplateId)
  if (picked) return { template: picked, origin: 'event' }

  const calendarId = typeof event.calendar === 'string' ? event.calendar : event.calendar?.id
  const calendarDefault = findTemplate(calendarsWithTemplates?.find((cwt) => cwt.calendarId === calendarId)?.templateId)
  if (calendarDefault) return { template: calendarDefault, origin: 'calendar' }

  const accountDefault = templates?.find((t) => t.is_default)
  return accountDefault ? { template: accountDefault, origin: 'account' } : null
}

/**
 * Mirrors the server's `getForEvent` order: picked template, calendar default, account default.
 * `templates` holds only the user's live templates, like the server's lookup, so the same walk
 * also tells where the server's own `template_language` came from.
 */
export function resolveEventTemplateLanguage(input: EventTemplateLanguageInput): EventTemplateLanguage {
  const { selectedTemplateId, event, templates, calendarsWithTemplates } = input
  const effective = findEffectiveTemplate(input)
  const pickUnchanged = (selectedTemplateId ?? null) === (event.message_template_id ?? null)
  const language =
    pickUnchanged && event.template_language !== undefined
      ? (event.template_language ?? null)
      : (effective?.template.language ?? null)
  const listsLoaded = templates !== undefined && calendarsWithTemplates !== undefined
  return { language, origin: language && listsLoaded ? (effective?.origin ?? null) : null }
}
